import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core'
import { BehaviorSubject, Observable, of, ReplaySubject, Subject } from 'rxjs'
import { map, takeUntil } from 'rxjs/operators'
import { MoreNodesSelected, VideoEndedEvent } from '../../../../collections/collections.models'
import { trackById } from '../../../../shared/utility-functions/track-by-id.utility'
import { Node } from 'src/io/models/node'
import { CollectionChildNodesRequestParams } from 'src/io/models/http/collection-child-nodes-request-params'
import {
  FilterModel,
  LinkModel,
  SearchModel,
  SectionModel,
  TextModel,
  ViewComponentModels
} from '@flocasts/experience-service-types'
import { AthleteResults, isExperienceStatX } from 'src/app/collections/collection.facade'
import { isFiltersSection, isSearchModel, isTextModel } from 'src/app/shared/functions/type-guards'
import { SearchFiltersService } from 'src/app/singleton-services/search-filters.service'

/**
 * Node List Base Type, extended by @NodeListComponentProps , which (potentially) uses statx results data, and
 * also extended by @ExperienceResultsNodeListComponentProps , which uses Experience Service results. This is temporary
 * until the new athlete/team profile component is created. @See {@link https://flocasts.atlassian.net/browse/CXP-1001}
 */
export interface NodeListComponentBase {
  nodes: ReadonlyArray<Node>
  nodesRequestParams: CollectionChildNodesRequestParams
  selectedNode?: Node
  // TODO rename to athleteResults
  results?: AthleteResults | Array<SectionModel<ViewComponentModels> | SearchModel | TextModel>
}
export interface NodeListComponentProps extends NodeListComponentBase {
  results?: AthleteResults
}

export interface ExperienceResultsNodeListComponentProps extends NodeListComponentBase {
  results: Array<SectionModel<ViewComponentModels> | SearchModel | TextModel>
}

@Component({
  selector: 'flo-node-list',
  templateUrl: `./node-list.component.html`,
  styleUrls: ['./node-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NodeListComponent implements OnDestroy {
  private ngOnDestroy$ = new ReplaySubject<void>(1)

  public readonly props$ = new ReplaySubject<NodeListComponentProps | ExperienceResultsNodeListComponentProps>(1)
  @Input()
  set props(props: NodeListComponentBase) {
    if (!!props) {
      // This is less than ideal, but it helps get our types right. The ngSwitch in the template should be switching on
      // the type of the data, not on a property sent in from a consuming component. Luckily this is temporary, as we
      // are migrating to a new Experience Service powered athlete/team profile parent component
      if (props.results && isExperienceStatX(props.results)) {
        this.props$.next(props as ExperienceResultsNodeListComponentProps)
      }
      this.props$.next(props as NodeListComponentProps)
    }
  }

  @Output()
  public readonly action$ = new Subject<MoreNodesSelected | VideoEndedEvent>()

  public readonly selectMoreNodes = () => this.action$.next(new MoreNodesSelected())

  public readonly listItemProps$ = this.props$.pipe(map(props => props.nodes))

  public nodeType$ = this.props$.pipe(map(props => props.nodesRequestParams.type))
  public readonly shouldShowPaginationButton$ = this.props$.pipe(
    map(props => {
      return props.nodesRequestParams.page < props.nodesRequestParams.pages
    })
  )

  public isLoading$ = this.props$.pipe(
    // @ts-expect-error
    map(props => props.nodes && props.nodes[0] && props.nodes[0].type !== props.nodesRequestParams.type)
  )

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }

  public trackById = trackById

  /**
   * Provide single obervable for *ngIf in template to wrap flo-search-filters
   */
  public searchFilters$: Observable<
    | {
        search: SearchModel | undefined
        filters: ReadonlyArray<FilterModel | LinkModel> | undefined
        resultsHeader: string | null | undefined
      }
    | undefined
  > = this.props$.pipe(
    map(props => {
      if (props.results && Array.isArray(props.results)) {
        const filters = props.results.find(isFiltersSection)?.items
        const search = props.results.find(isSearchModel)
        const resultsHeader = props.results.find(isTextModel)
        if (filters || search) {
          return {
            search,
            filters: filters && filters.length > 0 ? filters : undefined,
            resultsHeader: resultsHeader?.text
          }
        }
        return undefined
      }
      return undefined
    })
  )

  constructor(private readonly searchFiltersService: SearchFiltersService) {}

  public isFilterOrSearchLoading$ = this.searchFiltersService.isFilterOrSearchLoading$.pipe(
    takeUntil(this.ngOnDestroy$)
  )
}
